import { ErrorMessage, SuccessMessage } from "#root/components/alert";
import { disappear } from "#root/components/disappear";
import { Form } from "#root/components/form";
import * as Fields from "#root/components/form/fields";
import { GlyphLoading } from "#root/components/glyphs";
import { type CredentialRow, getCredentialCollectionForDeployment } from "#root/schema/credentials";
import { useData } from "#root/utility/hooks";
import { mergeUpdate } from "#root/utility/merge-update";
import type { RecordModificationResponse } from "#root/utility/remote-data";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import PageLayout from "./layout";

export default function CredentialsPage({ accountId, deploymentId }): ReactNode {
  const collection = useMemo(() => getCredentialCollectionForDeployment(accountId, deploymentId), [accountId, deploymentId]);

  const [credentials, setCredentials] = useData(async () => {
    const { rows } = await collection.list();
    return rows;
  }, [collection]);

  const handleUpdate = useCallback(
    (update: RecordModificationResponse<CredentialRow>) => {
      setCredentials(mergeUpdate(credentials!, update));
    },
    [setCredentials, credentials],
  );

  if (!credentials) {
    return (
      <PageLayout title="Credentials">
        <section>
          <GlyphLoading/>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Manage Credentials">
      <section>
        <h1>Manage Credentials</h1>
        <p>To access a CloudQuote API, you must authenticate using either an application key, or by connecting from a whitelisted IP or referring domain</p>
        <p>
          For more information about managing your CloudQuote credentials, please refer to the <a href="/article/18">Access and Authentication Guide</a>
        </p>
        <CredentialAdd onUpdate={handleUpdate} collection={collection}/>
        <CredentialList onUpdate={handleUpdate} credentials={credentials} collection={collection}/>
      </section>
    </PageLayout>
  );
}

function CredentialList({
                          collection,
                          onUpdate,
                          credentials,
                        }: {
  collection: ReturnType<typeof getCredentialCollectionForDeployment>;
  onUpdate: (update: RecordModificationResponse<CredentialRow>) => void;
  credentials: CredentialRow[];
}) {
  const credentialsByType = useMemo(() => {
    const credentialsByType: Record<string, Array<CredentialRow>> = {};
    for (const cred of credentials.sort((a, b) => (a.value ? a.value.localeCompare(b.value) : 0))) {
      const dest = credentialsByType[cred.type] || (credentialsByType[cred.type] = []);
      dest.push(cred);
    }

    return credentialsByType;
  }, [credentials]);

  const handleDelete = useCallback(
    async evnt => {
      const { id } = evnt.target.dataset;
      try {
        onUpdate(await collection.delete(id));
      } catch (error) {
        console.log("Error running delete operation:", error);
      }
    },
    [collection, onUpdate],
  );

  return (
    <div className="credential-list">
      {credentialsByType.KEYEDORIGIN && (
        <div className="credential-block domains">
          <h2>CORS Origin Domains</h2>
          <ul>
            {credentialsByType.KEYEDORIGIN.map(({ id, value }) => (
              <li key={id} className="key">
                {value}{" "}
                <a data-id={id} onClick={handleDelete}>
                  Remove Domain
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {credentialsByType.PRIVATEKEY && (
        <div className="credential-block keys">
          <h2>Private Application Keys</h2>
          <ul>
            {credentialsByType.PRIVATEKEY.map(({ id, value }) => (
              <li key={id} className="key">
                {value}{" "}
                <a data-id={id} onClick={handleDelete}>
                  Revoke Key
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {credentialsByType.PUBLICKEY && (
        <div className="credential-block keys">
          <h2>Public Application Keys</h2>
          <ul>
            {credentialsByType.PUBLICKEY.map(({ id, value }) => (
              <li key={id} className="key">
                {value}{" "}
                <a data-id={id} onClick={handleDelete}>
                  Revoke Key
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CredentialAdd({ collection, onUpdate }) {
  const [disabled, setDisabled] = useState<boolean>(false);
  const [message, setMessage] = useState<ReactNode | null>(null);
  const [version, setVersion] = useState(0);

  const [type, setType] = useState<string | undefined>(undefined);

  const handleChange = useCallback(({ type }: { type?: string }) => {
    setType(type!);
  }, []);

  const credentials = useMemo(() => {
    if (type === "PRIVATEKEY") {
      return { type, value: Math.random().toString(36).slice(2) };
    }

    if (type === "PUBLICKEY") {
      return { type, value: Math.random().toString(36).slice(2) };
    }

    return { type };
  }, [type]);

  const handleSubmit = useCallback(
    async (data: CredentialRow) => {
      setDisabled(true);
      try {
        const { row } = await collection.add(data);
        setMessage(disappear(<SuccessMessage>Credential has been added</SuccessMessage>));
        onUpdate(row);
        setType(undefined);
        setVersion(version + 1);
      } catch (error) {
        setMessage(disappear(<ErrorMessage error/>));
      }

      setDisabled(false);
    },
    [onUpdate, version, collection],
  );

  const AddComponent = type ? CredentialAdder[type] : undefined;
  return (
    <div className="credential-add">
      <h2>Add Credentials</h2>
      <Form key={version} data={credentials} disabled={disabled} onChange={handleChange} onSubmit={handleSubmit}>
        {message}
        <Fields.Set name="type">
          <label>Choose Credential Type</label>
          <Fields.Select>
            <Fields.Option value={null}/>
            <Fields.Option value="PRIVATEKEY">Private Application Key</Fields.Option>
            <Fields.Option value="PUBLICKEY">Public CORS Key</Fields.Option>
            <Fields.Option value="KEYEDORIGIN">CORS Origin Domain</Fields.Option>
          </Fields.Select>
          <Fields.Validity/>
        </Fields.Set>
        {AddComponent && <AddComponent/>}
      </Form>
    </div>
  );
}

// noinspection JSUnusedGlobalSymbols
const CredentialAdder: Record<string, () => ReactNode> = {
  KEYEDORIGIN() {
    return (
      <div className="credential-add-block domain">
        <Fields.Set name="value">
          <label>Domain Name</label>
          <Fields.Domain/>
          <Fields.Validity/>
        </Fields.Set>
        <Fields.Submit>Add Domain to Whitelist</Fields.Submit>
      </div>
    );
  },
  PRIVATEKEY() {
    return (
      <div className="credential-add-block private-key">
        <Fields.Set name="value">
          <label>Auto-Generated Private Key</label>
          <Fields.UUID dirty/>
          <Fields.Validity/>
        </Fields.Set>
        <Fields.Submit>Save Private Key</Fields.Submit>
      </div>
    );
  },
  PUBLICKEY() {
    return (
      <div className="credential-add-block public-key">
        <Fields.Set name="value">
          <label>Auto-Generated Public Key</label>
          <Fields.UUID dirty/>
          <Fields.Validity/>
        </Fields.Set>
        <Fields.Submit>Save Public Key</Fields.Submit>
      </div>
    );
  },
};
